import { useCallback, useEffect, useMemo, useState } from 'react'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import { Button, Pressable, TextInput } from '../Common'
import { useToast } from '../../context/useToast'
import { useTimeline } from '../../context/useTimeline'
import { useNls } from '../../nls/nlsProvider'
import {
  InventoryScanError,
  ResourceMismatchError,
  type StockScanDirection,
  type StockScanTargetType,
} from '../../services/InventoryScannerService'
import { useStockScan } from '../../hooks/useStockScan'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { HelperWorkspaceSummary } from '../../services/helper/helperStore'
import { registerSwitchGuard } from '../../services/workspaceSwitch/switchGuards'
import { BarcodeScannerShell } from './BarcodeScannerShell'
import { HelperEmptyState } from './HelperEmptyState'
import {
  buildHelperScanTargets,
  filterHelperScanTargets,
  type HelperScanTarget,
} from './helperControlsModel'

type HelperActor = { id: string; name: string } | null

type PendingScan = {
  barcode: string
  action: StockScanDirection
  workspaceId: string
}

const targetIcon = (type: HelperScanTarget['type']) => {
  if (type === 'WORKSPACE') return <Inventory2RoundedIcon fontSize="small" />
  if (type === 'CHECKLIST') return <TaskAltRoundedIcon fontSize="small" />
  return <AccountTreeRoundedIcon fontSize="small" />
}

export const HelperScanTab = ({
  workspaceId,
  workspaces,
  items,
  actor,
}: {
  workspaceId: string | null
  workspaces: HelperWorkspaceSummary[]
  items: HelperAssignedItem[]
  actor: HelperActor
}) => {
  const { t } = useNls()
  const { elements } = useTimeline()
  const { showToast } = useToast()
  const [scannerOpen, setScannerOpen] = useState(false)
  const [pendingScan, setPendingScan] = useState<PendingScan | null>(null)
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)

  const [prevWorkspaceId, setPrevWorkspaceId] = useState(workspaceId)
  if (workspaceId !== prevWorkspaceId) {
    setPrevWorkspaceId(workspaceId)
    if (pendingScan) setPendingScan(null)
  }

  const workspace = useMemo(
    () => workspaces.find((entry) => entry.id === workspaceId) ?? null,
    [workspaceId, workspaces],
  )

  const targets = useMemo(() => buildHelperScanTargets({
    workspaceId,
    workspaceName: workspace?.name,
    items,
    elements,
    t,
  }), [elements, items, t, workspace?.name, workspaceId])

  const visibleTargets = useMemo(() => {
    return filterHelperScanTargets(targets, search)
  }, [search, targets])

  const handleScanned = useCallback((barcode: string, action: StockScanDirection) => {
    setScannerOpen(false)
    setPendingScan({ barcode, action, workspaceId: workspaceId ?? '' })
    setSearch('')
  }, [workspaceId])

  const { scanStock } = useStockScan({
    workspaceId,
    actor: actor ? { id: actor.id, name: actor.name, role: 'HELPER' } : null,
    mode: 'helper',
  })

  const executeCommitScan = useCallback(async (target: HelperScanTarget) => {
    if (!workspaceId || !actor || !pendingScan) {
      throw new Error('Scan commit missing prerequisites')
    }
    if (busy) {
      throw new Error('Scanner is busy')
    }
    setBusy(true)
    try {
      const result = await scanStock(pendingScan.barcode, pendingScan.action, {
        taskId: target.type === 'CHECKLIST' ? target.taskId ?? undefined : target.type === 'WORKSPACE' ? undefined : target.id,
        checklistItemId: target.type === 'CHECKLIST' ? target.id : undefined,
        targetType: target.type as StockScanTargetType,
        targetId: target.id,
        targetTitle: target.title,
      })
      const verb = result.action === 'IN' ? 'scanned in' : 'scanned out'
      showToast(`${result.item.name} ${verb}.`, 'ghost')
      setPendingScan(null)
    } finally {
      setBusy(false)
    }
  }, [actor, busy, pendingScan, scanStock, showToast, workspaceId])

  const commitScan = useCallback(async (target: HelperScanTarget) => {
    try {
      await executeCommitScan(target)
    } catch (error) {
      const message = error instanceof ResourceMismatchError
        ? error.message
        : error instanceof InventoryScanError
          ? error.message
          : error instanceof Error
            ? error.message
            : t('NLSHelperToastInventoryScanFailed')
      showToast(message, 'error')
    }
  }, [executeCommitScan, showToast, t])

  useEffect(() => {
    if (!pendingScan) return
    return registerSwitchGuard({
      id: 'helper-pending-scan',
      describe: () => (pendingScan ? t('NLSHelperPendingScanGuardDescription', { barcode: pendingScan.barcode }) : null),
      save: async () => {
        if (!pendingScan) return
        const target = targets.find((entry) => entry.type === 'WORKSPACE' && entry.id === pendingScan.workspaceId)
        if (!target) {
          throw new Error(`Workspace target for ${pendingScan.workspaceId} not found`)
        }
        await executeCommitScan(target)
      },
      discard: () => {
        setPendingScan(null)
      },
    })
  }, [executeCommitScan, pendingScan, t, targets])

  if (!workspaceId) {
    return (
      <section className="flex-1 grid place-items-center rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-6 text-center">
        <p className="text-sm text-[var(--muted)]">{t('NLSHelperPickWorkspaceBeforeScan')}</p>
      </section>
    )
  }

  return (
    <section className="flex-1 flex flex-col gap-4 min-h-0" data-testid="helper-scan-tab">
      <div className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 flex items-center gap-3">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-r from-[var(--btn-primary-from)] to-[var(--btn-primary-to)] text-[var(--on-accent)]">
          <QrCodeScannerRoundedIcon />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">{t('NLSHelperScanTitle')}</p>
          <h2 className="text-base font-semibold text-[var(--text-strong)] truncate">{workspace?.name ?? t('NLSHelperScanWorkspaceTitleFallback')}</h2>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => setScannerOpen(true)}
          data-testid="helper-scan-start"
        >
          {t('NLSHelperScanAction')}
        </Button>
      </div>

      {pendingScan ? (
        <div className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 flex flex-col gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent-strong)]">{t('NLSHelperScanChooseTarget')}</p>
            <h3 className="text-sm font-semibold text-[var(--text-strong)]">
              {pendingScan.action === 'IN' ? t('NLSHelperScanIn') : t('NLSHelperScanOut')} · {pendingScan.barcode}
            </h3>
          </div>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={t('NLSHelperScanSearchPlaceholder')}
          />
          <div className="max-h-[48vh] overflow-y-auto flex flex-col gap-2 pr-1">
            {visibleTargets.map((target) => (
              <Pressable
                key={`${target.type}:${target.id}`}
                data-testid="helper-scan-target-row"
                disabled={busy}
                onClick={() => { void commitScan(target) }}
                className="px-3 py-2 text-left flex items-center gap-3 disabled:opacity-50"
              >
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--surface-muted)] text-[var(--accent-strong)]">
                  {targetIcon(target.type)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-[var(--text-strong)]">{target.title}</span>
                  <span className="block truncate text-xs text-[var(--muted)]">{target.subtitle}</span>
                </span>
              </Pressable>
            ))}
            {visibleTargets.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--muted)]">{t('NLSHelperScanNoMatchingTarget')}</p>
            ) : null}
          </div>
          <Button
            variant="tertiary"
            size="md"
            onClick={() => setPendingScan(null)}
          >
            {t('NLSCommonActionCancel')}
          </Button>
        </div>
      ) : (
        <HelperEmptyState
          title={t('NLSHelperScanIntro')}
          body={t('NLSHelperScanTargetsBody')}
        />
      )}

      <BarcodeScannerShell
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScanned}
      />
    </section>
  )
}
